import { z } from 'zod';
import { isoDateTime } from './common';

/**
 * Forum / member-publications post.
 *
 * Admin-authored content broadcast to enrolled members. Read-only for members
 * (no comments in v1). Gated: a member sees a post only if they're verified,
 * enrolled in ≥1 formation, and the post targets them.
 *
 * `audienceKeys` is a derived query field so members can fetch their feed with a
 * single `array-contains-any` query (and the security rule can match the same):
 *   - audience 'all'        → audienceKeys = ['all']
 *   - audience 'formations' → audienceKeys = [...formationIds]
 * A member queries with `['all', ...their enrolledFormationIds]`.
 */
export const forumPostKindSchema = z.enum(['article', 'message']);
export type ForumPostKind = z.infer<typeof forumPostKindSchema>;

/**
 * - 'public'     → visible to EVERYONE, including anonymous visitors (grand public).
 * - 'all'        → all enrolled members.
 * - 'formations' → members enrolled in the listed formations.
 */
export const forumPostAudienceSchema = z.enum(['public', 'all', 'formations']);
export type ForumPostAudience = z.infer<typeof forumPostAudienceSchema>;

export const forumAttachmentSchema = z.object({
  url: z.string().url(),
  name: z.string().min(1).max(200),
});
export type ForumAttachment = z.infer<typeof forumAttachmentSchema>;

export const forumPostInputSchema = z.object({
  title: z.string().min(1).max(200),
  kind: forumPostKindSchema.default('article'),
  /** Sanitized HTML authored via the admin WYSIWYG editor. */
  body: z.string().max(100000),
  audience: forumPostAudienceSchema,
  /** Target formation slugs when audience === 'formations'. Empty for 'all'. */
  formationIds: z.array(z.string()).default([]),
  /** Derived query key — maintained on write (see repository). */
  audienceKeys: z.array(z.string()).default([]),
  coverImageUrl: z.string().url().nullable().default(null),
  attachments: z.array(forumAttachmentSchema).default([]),
  published: z.boolean().default(false),
  pinned: z.boolean().default(false),
});
export type ForumPostInput = z.infer<typeof forumPostInputSchema>;

export const forumPostSchema = forumPostInputSchema.extend({
  id: z.string(),
  authorUid: z.string().nullable().default(null),
  authorName: z.string().optional(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export type ForumPost = z.infer<typeof forumPostSchema>;

/**
 * Compute `audienceKeys` from the audience + formationIds. Single source of
 * truth so the admin write and the member query/rule stay aligned.
 */
export function computeAudienceKeys(
  audience: ForumPostAudience,
  formationIds: string[],
): string[] {
  if (audience === 'public') return ['public'];
  if (audience === 'all') return ['all'];
  return Array.from(new Set(formationIds));
}
